import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CircleAlert, Info, Pencil, Split, X } from 'lucide-react';
import { transportApi } from '../api/transport';
import type {
  LegKind,
  MyVehicles,
  RoundTrip,
  SeatLedgerLine,
  TransportLeg,
  TransportOffer,
  TransportPlan,
} from '../api/transport';
import { Button } from '../ui/Button';
import { ManageBlock } from '../ui/DocketRow';
import { Chip } from '../ui/Card';
import { SelectField, TextField } from '../ui/Field';
import { MapLink } from '../ui/MapLink';
import { formatInZone, toWallClockInZone } from '../lib/datetime';

/**
 * Which thing the edit form is open on. A leg is keyed by its own id; the round trip is the pair,
 * so it takes a name of its own rather than borrowing the outbound's — the two are different
 * subjects and one form each.
 */
const ROUND_TRIP = 'round-trip';

/**
 * Getting there and back — the transport plan on the event page, beside the rest of the answers.
 *
 * **The number a leader opens the app to find is three lines:** how many riders each leg needs, how
 * many seats have been offered, and whether that adds up. Everything else here exists to make those
 * three numbers mean something specific.
 *
 * **A shortfall is a state, never an error.** Every trip is short until the Wednesday, so the count
 * is quiet by default and gains weight as the event approaches — a red banner three weeks out is
 * one everybody learns to scroll past, at which point the real one is scrolled past too.
 *
 * **An event with no legs renders nothing at all.** A Monday meeting needs no transport plan, and a
 * section with three empty rows on one is noise that trains leaders to ignore the section. A round
 * trip becoming cheap to create does not soften that: nothing is offered unprompted.
 *
 * **The exception is an event that already says it departs** (TROOP-241). `Event.DeparturePlace` was
 * a second, unreconciled copy of a leg's *From* and it is gone; the place now lives on the trip, so
 * an event carrying a departure time and no trips is a leader who has said *we leave at five* with
 * nowhere left to say where from. That one opens the round-trip form rather than the resting
 * sentence, prefilled from what the event already answers — the removal of a field has to show where
 * it went, or it is a capability that quietly stopped existing. It is still a form and not a write:
 * nothing is created until somebody submits it.
 *
 * **It reaches only the events that ask for travel**, because this component mounts only when the
 * docket carries a `travel` section and the server withholds that when `RequiresTravel` is false —
 * while the event form's *Depart* and *Return* boxes are not behind that toggle. An event with the
 * section switched off can therefore state a departure time and has nowhere to state a place. That
 * is deliberate and it is not fixed here: see `docs/architecture.md` on why a time must not switch
 * a section back on.
 *
 * **Most trips are a there-and-back, so that is what the section opens on** (TROOP-239). The
 * three-way model underneath is unchanged and not in question — a parent who drives up on Friday is
 * very often not driving home on Sunday. What changed is that recording the ordinary case no longer
 * takes two passes through a form asking the same four questions twice, which is what left events
 * carrying a *Getting there* and no *Getting home* while the screen rendered them as finished.
 *
 * The same component serves the member's page and the parent's. `unitId` is null on `/family`,
 * which is what picks the guardian's door — never a second set of rules, only a second path.
 */
export function EventTransport({
  unitId,
  eventId,
  timeZoneId,
  departsAt,
  returnsAt,
  place,
  canManage,
  people,
  onChanged,
}: {
  /** Null on `/family`. A guardian holds no membership and would be 404'd under `/units/`. */
  unitId: string | null;
  eventId: string;
  /** The event's effective zone, already resolved server-side. Every time below renders through it. */
  timeZoneId: string;
  /**
   * The event's own departure and return, as instants. They prefill the prompt described above and
   * decide whether it appears at all — an event that says it departs is one whose trip is worth
   * asking about, and one that does not is a Monday meeting.
   */
  departsAt?: string | null;
  returnsAt?: string | null;
  /**
   * Where the event is, as the *Where* row resolves it — the name, or the address when there is no
   * name. Prefills the prompt's *To*, and nothing else here reads it.
   */
  place?: string | null;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canManage: boolean;
  /**
   * Who this caller may answer for — themselves, and their own children. Empty hides the
   * self-transport controls entirely rather than showing ones that would 404.
   */
  people?: { personId: string; displayName: string }[];
  /** Tells the docket row above to re-read its seat counts (TROOP-116). */
  onChanged?: () => void;
}) {
  const [plan, setPlan] = useState<TransportPlan | null>(null);
  const [mine, setMine] = useState<MyVehicles | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  /**
   * *Break into separate trips*, and back. **A presentation switch and not a data one** — the rows
   * are the same rows either way, so it lives here in component state and writes nothing. It is
   * what reaches the per-leg controls: a third leg, a label, and removing one half of a pair all
   * happen in the broken-out view rather than needing round-trip equivalents of their own.
   */
  const [separate, setSeparate] = useState(false);

  /**
   * Switching views closes whatever form was open, rather than leaving `editing` pointing at a leg
   * the other view does not render — which would reopen the form on the way back with a half-typed
   * destination in it, minutes later and out of context.
   */
  const showSeparately = (apart: boolean) => {
    setEditing(null);
    setSeparate(apart);
  };

  const load = useCallback(async () => {
    try {
      const [loaded, vehicles] = await Promise.all([
        transportApi.plan(unitId, eventId),
        transportApi.myVehicles().catch(() => null),
      ]);

      setPlan(loaded);
      setMine(vehicles);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the transport plan.');
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The prompt opens itself when the payload lands on an event that departs and holds no trips. A
   * latch rather than state, so a later reload does not reopen a form somebody closed.
   *
   * **Once per mounting, which is not once per visit.** `DocketRow` unmounts this panel when the row
   * collapses, so the ref is fresh on the next expand and the prompt asks again — the right shape for
   * a question that is still unanswered, and the reason this is *not* `EventDocket`'s `seeded` latch,
   * which survives because that component stays mounted for the life of the page.
   */
  const prompted = useRef(false);

  useEffect(() => {
    if (!plan || prompted.current) return;
    prompted.current = true;

    if (plan.legs.length === 0 && departsAt && canManage && unitId !== null) {
      setAdding(true);
    }
  }, [plan, departsAt, canManage, unitId]);

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      await load();
      onChanged?.();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * What a form calls once its own save has landed. Separate from {@link act} because the forms
   * report their own refusals inline and keep what was typed — routing them through `act` would
   * move the sentence to the top of the section and clear the boxes under it, which on a rejected
   * time means retyping four fields to fix one.
   */
  const reload = async () => {
    await load();
    onChanged?.();
  };

  if (!plan) return null;

  const hasLegs = plan.legs.length > 0;

  /**
   * **Every managing control is gated on the unit as well as on the permission.** `unitId` is null
   * on `/family` and every write endpoint here is unit-scoped, so a control drawn for a guardian
   * would 404 for the person it was drawn for — a control with nothing behind it, which the working
   * rules put ahead of a missing one in badness.
   */
  const canWrite = canManage && unitId !== null;

  // The honest resting state. Nothing renders for an event nobody drives to, and a leader who
  // wants one adds it deliberately.
  if (!hasLegs && !canWrite) return null;

  /**
   * The departure this event has nowhere left to explain: a time it leaves, no trips, and somebody
   * reading who may write one. Null when there is no prompt — the instant rather than a boolean, so
   * the sentence and the prefill below can read it without asserting it is there.
   *
   * It writes nothing, and a reader who cannot manage the plan never sees it.
   */
  const departureWithNoTrip = !hasLegs && canWrite && departsAt ? departsAt : null;

  /**
   * The event's own times, as the round-trip form's two boxes. `toWallClockInZone` and never the
   * browser's clock: prefilling from the viewer's zone opens the form on a departure an hour out,
   * and saving it makes that wrong time the trip's record.
   *
   * *Gets back at* takes the event's return because it is the same fact — when the family is home
   * again — and half a prefilled form is four boxes a leader has to check rather than two. *To* is
   * the event's own place for the same reason: the destination was never the fact that lost its home,
   * and leaving it blank beside a known answer is a box a leader fills in by copying what is on the
   * screen above. **From stays empty on purpose** — it is the question being asked.
   */
  const fromTheEvent = departureWithNoTrip
    ? {
        from: '',
        to: place ?? '',
        leavesAt: toWallClockInZone(departureWithNoTrip, timeZoneId),
        getsBackAt: returnsAt ? toWallClockInZone(returnsAt, timeZoneId) : '',
      }
    : undefined;

  const ledgerFor = (legId: string) => plan.ledger.find((l) => l.legId === legId);
  const legFor = (legId: string) => plan.legs.find((l) => l.id === legId);
  const offersFor = (legId: string) => plan.offers.filter((o) => o.legId === legId);

  const shared = {
    timeZoneId,
    unitId,
    eventId,
    vehicles: mine?.vehicles.filter((v) => !v.retiredAt) ?? [],
    minimumSeats: mine?.minimumSeatingCapacity ?? 2,
    people: people ?? [],
    busy,
    act,
  };

  const roundTrip = plan.roundTrip;
  const outbound = roundTrip ? legFor(roundTrip.outboundLegId) : undefined;
  const back = roundTrip ? legFor(roundTrip.returnLegId) : undefined;

  // Whether the section *renders* as one, which is the server's derived answer plus this reader's
  // own choice to see the halves. Both legs are looked up rather than assumed: a plan that says
  // round trip and a legs array missing one of them is a bug worth failing visibly on.
  const asRoundTrip = Boolean(roundTrip && outbound && back && !separate);

  // A round trip can only be added when the event holds neither half yet. Once it holds one, the
  // per-leg form is the honest control -- and the same asymmetry it has always rendered, where a
  // kind already on the event is not offered again rather than being offered and refused.
  const canAddRoundTrip = !plan.legs.some((l) => l.kind !== 'Excursion');

  return (
    <section className="flex flex-col gap-4">

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
          {error}
        </p>
      )}

      {!hasLegs && (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
          {departureWithNoTrip ? (
            <>
              This one leaves at {formatInZone(departureWithNoTrip, timeZoneId)}, and nothing yet
              says where from. Fill in the trip below — the way there and the way home are recorded
              together.
            </>
          ) : (
            <>
              No trips yet. Add one when the unit needs to work out who is driving — a meeting at the
              usual place does not need this.
            </>
          )}
        </p>
      )}

      {asRoundTrip && roundTrip && outbound && back && (
        <RoundTripCard
          {...shared}
          trip={roundTrip}
          outbound={outbound}
          back={back}
          ledgerFor={ledgerFor}
          offersFor={offersFor}
          canWrite={canWrite}
          editing={editing === ROUND_TRIP}
          onEdit={() => setEditing(ROUND_TRIP)}
          onDoneEditing={() => setEditing(null)}
          onSeparate={() => showSeparately(true)}
          reload={reload}
        />
      )}

      {!asRoundTrip &&
        plan.legs.map((leg) => (
          <LegCard
            key={leg.id}
            {...shared}
            leg={leg}
            ledger={ledgerFor(leg.id)}
            offers={offersFor(leg.id)}
            canWrite={canWrite}
            editing={editing === leg.id}
            onEdit={() => setEditing(leg.id)}
            onDoneEditing={() => setEditing(null)}
            reload={reload}
          />
        ))}

      {/*
        The way back to one card, offered only when there is one to go back to. Nothing was written
        on the way out, so nothing is restored on the way in.
      */}
      {!asRoundTrip && roundTrip && outbound && back && (
        <div>
          <Button variant="secondary" onClick={() => showSeparately(false)}>
            Show as one round trip
          </Button>
        </div>
      )}

      {canWrite && (
        <ManageBlock
          label="Add a trip"
          open={adding}
          onToggle={() => setAdding((was) => !was)}
        >
          {canAddRoundTrip && !separate ? (
            <RoundTripForm
              submitLabel="Add this trip"
              busy={busy}
              initial={fromTheEvent}
              onSubmit={(body) => transportApi.addRoundTrip(unitId!, eventId, body)}
              onDone={async (saved) => {
                setAdding(false);
                if (saved) {
                  await load();
                  onChanged?.();
                }
              }}
              onSeparate={() => showSeparately(true)}
            />
          ) : (
            <AddLegForm
              unitId={unitId!}
              eventId={eventId}
              timeZoneId={timeZoneId}
              existing={plan.legs}
              busy={busy}
              onDone={async (saved) => {
                setAdding(false);
                if (saved) {
                  await load();
                  onChanged?.();
                }
              }}
            />
          )}
        </ManageBlock>
      )}
    </section>
  );
}

/** What the two cards below both hand to a leg's body. */
interface LegContext {
  timeZoneId: string;
  unitId: string | null;
  eventId: string;
  vehicles: { id: string; description: string; seatingCapacity: number }[];
  /** From the server, so the floor and what a seat means are decided in one place. */
  minimumSeats: number;
  people: { personId: string; displayName: string }[];
  busy: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}

function PlanCard({ children }: { children: ReactNode }) {
  return (
    <article
      className="flex flex-col gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
    >
      {children}
    </article>
  );
}

/**
 * One journey, one card, **two ledger lines**. TROOP-239.
 *
 * The two directions sit side by side and are never added, never averaged, and never resolved into
 * a single verdict about the trip. That is not a styling preference: twelve spare seats on the
 * Friday say nothing whatever about the Sunday, and a surplus cancelling a shortfall is how a
 * covered-looking plan leaves a Scout in a car park (TROOP-135).
 */
function RoundTripCard({
  trip,
  outbound,
  back,
  ledgerFor,
  offersFor,
  canWrite,
  editing,
  onEdit,
  onDoneEditing,
  onSeparate,
  reload,
  ...context
}: LegContext & {
  trip: RoundTrip;
  outbound: TransportLeg;
  back: TransportLeg;
  ledgerFor: (legId: string) => SeatLedgerLine | undefined;
  offersFor: (legId: string) => TransportOffer[];
  canWrite: boolean;
  editing: boolean;
  onEdit: () => void;
  onDoneEditing: () => void;
  onSeparate: () => void;
  reload: () => Promise<void>;
}) {
  const { timeZoneId, unitId, eventId, busy } = context;

  return (
    <PlanCard>
      {/*
        The times live with their direction below rather than being summarised here, and that is a
        correction rather than a layout preference. A header built from the two fields the round-trip
        form owns -- the outbound's departure and the return's arrival -- renders nothing at all for
        a return leg carrying only a departure, which is exactly what an event type lends
        (`LegAnchor.EventEnd`). A leader would read "no time home" off a plan that has one, and the
        only way to see it would be to break the trip apart.
      */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="t-body-strong">Round trip</h3>
        <Places from={trip.from} to={trip.to} />
      </div>

      {editing ? (
        <RoundTripForm
          submitLabel="Save this trip"
          busy={busy}
          initial={{
            from: trip.from ?? '',
            to: trip.to ?? '',
            leavesAt: outbound.departsAt ? toWallClockInZone(outbound.departsAt, timeZoneId) : '',
            getsBackAt: back.returnsAt ? toWallClockInZone(back.returnsAt, timeZoneId) : '',
          }}
          onSubmit={(body) => transportApi.updateRoundTrip(unitId!, eventId, body)}
          onDone={async (saved) => {
            onDoneEditing();
            if (saved) await reload();
          }}
        />
      ) : (
        canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={onEdit}>
              <Pencil size={16} aria-hidden />
              Edit this trip
            </Button>

            {/*
              Removing one half, renaming a leg or adding a Saturday shuttle all live in the
              broken-out view. One control gets you there rather than each of them growing a
              round-trip twin that would be a second place for the same rule.
            */}
            <Button variant="secondary" onClick={onSeparate}>
              <Split size={16} aria-hidden />
              Break into separate trips
            </Button>
          </div>
        )
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        {[
          { heading: 'Getting there', leg: outbound },
          { heading: 'Getting home', leg: back },
        ].map(({ heading, leg }) => (
          <div key={leg.id} className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="t-label" style={{ color: 'var(--color-text-secondary)' }}>
                {heading}
              </h4>
              {/* Whichever of the two times this leg actually carries — see the note above. */}
              <LegTimes leg={leg} timeZoneId={timeZoneId} />
            </div>

            <LegBody
              {...context}
              leg={leg}
              ledger={ledgerFor(leg.id)}
              offers={offersFor(leg.id)}
            />
          </div>
        ))}
      </div>
    </PlanCard>
  );
}

function LegCard({
  leg,
  ledger,
  offers,
  canWrite,
  editing,
  onEdit,
  onDoneEditing,
  reload,
  ...context
}: LegContext & {
  leg: TransportLeg;
  ledger: SeatLedgerLine | undefined;
  offers: TransportOffer[];
  canWrite: boolean;
  editing: boolean;
  onEdit: () => void;
  onDoneEditing: () => void;
  reload: () => Promise<void>;
}) {
  const { timeZoneId, unitId, eventId, busy, act } = context;

  return (
    <PlanCard>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="t-body-strong">{leg.label}</h3>
        <LegTimes leg={leg} timeZoneId={timeZoneId} />
      </div>

      <Places from={leg.originText} to={leg.destinationText} />

      {editing ? (
        /*
          Editing a leg that already exists -- TROOP-240. The endpoint has been there since
          TROOP-50 and nothing called it, so correcting a typo in a destination meant removing the
          leg and retyping it, which withdrew every offer made against it and had four drivers
          asked again. Nothing this form saves touches an offer, a declaration or the ledger.
        */
        <LegFields
          kind={leg.kind}
          timeZoneId={timeZoneId}
          submitLabel="Save this trip"
          busy={busy}
          initial={leg}
          onSubmit={(body) => transportApi.updateLeg(unitId!, eventId, leg.id, body)}
          onDone={async (saved) => {
            onDoneEditing();
            if (saved) await reload();
          }}
        />
      ) : (
        <LegBody {...context} leg={leg} ledger={ledger} offers={offers} />
      )}

      {canWrite && !editing && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={onEdit}>
            <Pencil size={16} aria-hidden />
            Edit this trip
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              act(async () => {
                const { offersWithdrawn } = await transportApi.removeLeg(unitId!, eventId, leg.id);
                if (offersWithdrawn > 0) {
                  // Never silent: the people who had offered need to know their offer went with it.
                  window.alert(
                    `${offersWithdrawn} offer${offersWithdrawn === 1 ? '' : 's'} withdrawn with it.`,
                  );
                }
              })
            }
            disabledReason={busy ? 'Saving…' : undefined}
          >
            <X size={16} aria-hidden />
            Remove this trip
          </Button>
        </div>
      )}
    </PlanCard>
  );
}

/**
 * Where a leg starts and where it ends, each with a way into the reader's own map app.
 *
 * **Both, one, or neither.** A bare `from → to` join renders a lone origin as an unadorned place
 * name under a heading saying "Round trip", which reads as the destination -- and a pair of legs
 * lent by an event type is exactly that case: the meeting place, and nowhere named to go yet. So a
 * place standing alone says which end of the trip it is. That rule was the round trip's; the leg
 * card joined the same two fields without it and had the same ambiguity, so there is one of these
 * rather than two renderings of one pair of fields.
 *
 * **One control per place** (TROOP-242), so a leg with a From and no To has one and not two —
 * `MapLink` renders nothing for a place that is not there, which is what makes that true here
 * without a branch.
 */
function Places({ from, to }: { from: string | null; to: string | null }) {
  if (!from && !to) return null;

  return (
    <div
      className="t-caption flex flex-wrap items-center gap-2"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      {from && <Place label={to ? from : `From ${from}`} place={from} />}

      {from && to && <span aria-hidden>→</span>}

      {to && <Place label={from ? to : `To ${to}`} place={to} />}
    </div>
  );
}

/**
 * One place and its control, as a single thing that wraps whole.
 *
 * Each pair is one flex item rather than two. Left loose in the row, a two-place leg put the second
 * *Open in Maps* on a line of its own under the first place -- a control sitting nearer the place it
 * is not about, which on a phone is the ordinary width rather than the edge case.
 */
function Place({ label, place }: { label: string; place: string }) {
  return (
    <span className="inline-flex items-center gap-2" style={{ maxWidth: '100%' }}>
      <span>{label}</span>
      <MapLink name={place} />
    </span>
  );
}

/**
 * Both of a leg's times, and either may stand alone.
 *
 * A round trip's return carries only *gets back at* — what time the cars pull out of camp on Sunday
 * is a question nobody was asked — so a renderer keyed on the departure alone would print nothing
 * for a leg that plainly has a time on it.
 */
function LegTimes({ leg, timeZoneId }: { leg: TransportLeg; timeZoneId: string }) {
  if (!leg.departsAt && !leg.returnsAt) return null;

  // Each time is labelled whenever it stands alone, because on its own a bare clock reading on a
  // return leg could equally be "we leave camp then" or "we are home then" — two facts the model
  // keeps apart and a leader plans around differently.
  const leaves = leg.departsAt && `Leaves ${formatInZone(leg.departsAt, timeZoneId)}`;
  const back =
    leg.returnsAt && `${leaves ? 'back' : 'Back'} ${formatInZone(leg.returnsAt, timeZoneId)}`;

  return (
    <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
      {[leaves, back].filter(Boolean).join(' · ')}
    </span>
  );
}

/**
 * A leg's own three answers: the seat line, who has offered, and what this reader can say about it.
 *
 * Shared by both cards on purpose. A round trip is a presentation of two legs, so the controls a
 * driver and a family reach inside one have to be the same controls — a second copy for the round
 * trip is where "offering on the way home" quietly stops working.
 */
function LegBody({
  leg,
  ledger,
  offers,
  unitId,
  eventId,
  vehicles,
  minimumSeats,
  people,
  busy,
  act,
}: LegContext & {
  leg: TransportLeg;
  ledger: SeatLedgerLine | undefined;
  offers: TransportOffer[];
}) {
  const [offering, setOffering] = useState(false);

  return (
    <>
      {ledger && <SeatCount line={ledger} />}

      {offers.length > 0 && (
        <ul className="flex flex-col gap-2">
          {offers.map((offer) => (
            <li key={offer.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="t-body">
                  {offer.driverName} — {offer.vehicleDescription}
                </span>
                {/* Including the driver's own -- TROOP-136. */}
                <Chip>{offer.seatsOffered} seats with them</Chip>
                {offer.licensePlate && (
                  <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    {offer.licensePlate}
                  </span>
                )}
              </div>

              {offer.note && (
                <p className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                  {offer.note}
                </p>
              )}

              {/*
                A planning fact, not a verdict. Neutral colour and an information icon rather than
                a warning one: for every other offer the opposite case was not checked, so anything
                that reads like a compliance tick here would be claiming more than the product
                knows.
              */}
              {offer.twoPeopleAdvisory && (
                <p
                  className="t-caption flex items-start gap-2"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Info size={14} aria-hidden style={{ marginTop: '2px', flexShrink: 0 }} />
                  {offer.twoPeopleAdvisory}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!offering &&
          (vehicles.length > 0 ? (
            <Button variant="secondary" onClick={() => setOffering(true)}>
              I can drive this one
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabledReason="Add a vehicle on your profile first, and confirm your licence."
            >
              I can drive this one
            </Button>
          ))}
      </div>

      {offering && (
        <OfferForm
          vehicles={vehicles}
          minimumSeats={minimumSeats}
          busy={busy}
          onCancel={() => setOffering(false)}
          onSubmit={async (vehicleId, seats, note) => {
            await act(() =>
              transportApi.offer(unitId, eventId, leg.id, {
                vehicleId,
                seatsOffered: seats,
                note,
              }),
            );
            setOffering(false);
          }}
        />
      )}

      {people.length > 0 && (
        <SeatNeedControls
          people={people}
          busy={busy}
          onSet={(personId, need) =>
            act(() => transportApi.setSeatNeed(unitId, eventId, leg.id, personId, need))
          }
        />
      )}
    </>
  );
}

/**
 * The three numbers, and the one that renders beside them rather than inside them.
 *
 * `unanswered` arrives as 0 for anybody who is not an adult officer, so a family sees whether
 * Sunday is short without seeing which four families are quiet.
 */
function SeatCount({ line }: { line: SeatLedgerLine }) {
  const short = -line.surplus;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/*
        Both numbers count people in cars: a driver is a rider, and their own seat is one of the
        seats (TROOP-136). "Travelling" rather than "riders" because a driver reading "22 riders"
        of a party of 22 would reasonably wonder whether they were in it.
      */}
      <span className="t-body">
        {line.riders} travelling, {line.seatsOffered} seat
        {line.seatsOffered === 1 ? '' : 's'}
      </span>

      <Chip>{line.isCovered ? 'Covered' : `Short by ${short}`}</Chip>

      {line.maybes > 0 && (
        <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
          {line.maybes} more if the maybes come
          {line.surplusWithMaybes < 0 && ` (short by ${-line.surplusWithMaybes} then)`}
        </span>
      )}

      {/*
        Beside the shortfall, never folded into it. "Short by 6, and 4 families have not replied" is
        a different situation from "short by 6", and the leader's next action differs in each.
      */}
      {line.unanswered > 0 && (
        <span
          className="t-caption flex items-center gap-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <CircleAlert size={14} aria-hidden />
          {line.unanswered} {line.unanswered === 1 ? 'family has' : 'families have'} not replied
        </span>
      )}
    </div>
  );
}

function OfferForm({
  vehicles,
  minimumSeats,
  busy,
  onCancel,
  onSubmit,
}: {
  vehicles: { id: string; description: string; seatingCapacity: number }[];
  minimumSeats: number;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (vehicleId: string, seats: number, note: string | null) => Promise<void>;
}) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id ?? '');
  const [seats, setSeats] = useState(String(vehicles[0]?.seatingCapacity ?? minimumSeats));
  const [note, setNote] = useState('');

  const chosen = vehicles.find((v) => v.id === vehicleId);
  const asked = Number(seats);

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(vehicleId, asked, note.trim() || null);
      }}
    >
      <SelectField
        label="Which car"
        value={vehicleId}
        onChange={(e) => {
          setVehicleId(e.target.value);
          const next = vehicles.find((v) => v.id === e.target.value);
          // Defaults to the whole car. The common case is all of it, and making a driver retype
          // the number they already entered once is how a form gets abandoned.
          if (next) setSeats(String(next.seatingCapacity));
        }}
      >
        {vehicles.map((v) => (
          <option key={v.id} value={v.id}>
            {v.description} ({v.seatingCapacity} seats including you)
          </option>
        ))}
      </SelectField>

      <TextField
        label="People this car can take, including you"
        type="number"
        min={minimumSeats}
        max={chosen?.seatingCapacity ?? minimumSeats}
        value={seats}
        onChange={(e) => setSeats(e.target.value)}
        hint={
          chosen
            ? `Lower it if you are carrying gear. Your car is recorded as holding ${chosen.seatingCapacity} ` +
              'people including you — to offer more, change the vehicle on your profile.'
            : undefined
        }
      />

      {/*
        Shown at the moment the offer is made, from the seat count alone and knowing nothing about
        who will ride. Advisory: the offer saves regardless.
      */}
      {asked === minimumSeats && (
        <p className="t-caption flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
          <Info size={14} aria-hidden style={{ marginTop: '2px', flexShrink: 0 }} />
          With two seats, this car carries you and one other person. That rider will need to be
          another adult or your own child.
        </p>
      )}

      <TextField
        label="Anything else"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        hint="Optional — “room for a bit of gear too”."
      />

      <div className="flex gap-2">
        <Button type="submit" disabledReason={busy ? 'Saving…' : undefined}>
          Offer these seats
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * One control per person, never one for the family: a Scout riding up with a friend while their
 * sibling comes in the family car is ordinary, and it is per leg because *"we'll bring him up
 * Friday, but can he ride home with somebody?"* is the commonest transport message in any troop.
 */
function SeatNeedControls({
  people,
  busy,
  onSet,
}: {
  people: { personId: string; displayName: string }[];
  busy: boolean;
  onSet: (personId: string, need: 'NeedsSeat' | 'SelfTransport') => Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-2">
      {people.map((person) => (
        <div key={person.personId} className="flex flex-wrap items-center gap-2">
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            {person.displayName}
          </span>
          <Button
            variant="secondary"
            onClick={() => void onSet(person.personId, 'SelfTransport')}
            disabledReason={busy ? 'Saving…' : undefined}
          >
            We'll bring them
          </Button>
          <Button
            variant="secondary"
            onClick={() => void onSet(person.personId, 'NeedsSeat')}
            disabledReason={busy ? 'Saving…' : undefined}
          >
            They need a seat
          </Button>
        </div>
      ))}
    </div>
  );
}

/**
 * Four boxes, and the pair of legs falls out of them. TROOP-239.
 *
 * **One form for adding and for editing**, because the mirroring rule has one home on the server
 * and a second form here would be the place it drifted. *Gets back at* is when the family is home
 * again — what time the cars leave camp on Sunday is a question this form does not ask, and so it
 * is a question it does not answer.
 *
 * The refusal keeps what was typed. A form that clears itself on a rejected time makes the leader
 * retype four boxes to fix one of them.
 */
function RoundTripForm({
  submitLabel,
  busy,
  initial,
  onSubmit,
  onDone,
  onSeparate,
}: {
  submitLabel: string;
  busy: boolean;
  initial?: { from: string; to: string; leavesAt: string; getsBackAt: string };
  onSubmit: (body: {
    from: string | null;
    to: string | null;
    leavesAt: string | null;
    getsBackAt: string | null;
  }) => Promise<unknown>;
  onDone: (saved: boolean) => Promise<void>;
  /** Absent in edit mode: breaking out is a control on the card, not on the form. */
  onSeparate?: () => void;
}) {
  const [from, setFrom] = useState(initial?.from ?? '');
  const [to, setTo] = useState(initial?.to ?? '');
  const [leavesAt, setLeavesAt] = useState(initial?.leavesAt ?? '');
  const [getsBackAt, setGetsBackAt] = useState(initial?.getsBackAt ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  return (
    <form
      className="flex flex-col gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
          await onSubmit({
            from: from.trim() || null,
            to: to.trim() || null,
            leavesAt: leavesAt || null,
            getsBackAt: getsBackAt || null,
          });
          await onDone(true);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'That did not save.');
        } finally {
          setSaving(false);
        }
      }}
    >
      <TextField
        label="From"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        hint="Where everybody sets off, and where they come back to."
      />
      <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} />

      <TextField
        label="Leaves at"
        type="datetime-local"
        value={leavesAt}
        onChange={(e) => setLeavesAt(e.target.value)}
        hint="Local time where the event is."
      />
      <TextField
        label="Gets back at"
        type="datetime-local"
        value={getsBackAt}
        onChange={(e) => setGetsBackAt(e.target.value)}
        hint="When everybody is home again."
      />

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabledReason={busy || saving ? 'Saving…' : undefined}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={() => void onDone(false)}>
          Cancel
        </Button>

        {onSeparate && (
          <Button type="button" variant="secondary" onClick={onSeparate}>
            <Split size={16} aria-hidden />
            Break into separate trips
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * Editing a leg that already exists — TROOP-240.
 *
 * **The kind is not here.** Turning an Outbound into a Return is a different leg with different
 * offers standing against it, and §15's partial unique index would refuse the move anyway, so it is
 * not offered rather than offered and refused.
 *
 * Nothing this form saves touches an offer, a seat declaration or the ledger. Correcting a
 * destination used to mean removing the leg and retyping it, which withdrew every offer made
 * against it and had four drivers asked again.
 */
function AddLegForm({
  unitId,
  eventId,
  timeZoneId,
  existing,
  busy,
  onDone,
}: {
  unitId: string;
  eventId: string;
  timeZoneId: string;
  existing: TransportLeg[];
  busy: boolean;
  onDone: (saved: boolean) => Promise<void>;
}) {
  const [kind, setKind] = useState<LegKind>('Outbound');

  // The asymmetry, rendered: two of the three are singular, so a kind already on the event is not
  // offered again rather than being offered and refused.
  const taken = new Set(existing.map((l) => l.kind));
  const available: LegKind[] = (['Outbound', 'Return', 'Excursion'] as LegKind[]).filter(
    (k) => k === 'Excursion' || !taken.has(k),
  );

  useEffect(() => {
    if (!available.includes(kind) && available.length > 0) setKind(available[0]);
  }, [available, kind]);

  return (
    <LegFields
      kind={kind}
      onKindChange={setKind}
      available={available}
      timeZoneId={timeZoneId}
      submitLabel="Add this trip"
      busy={busy}
      onSubmit={(body) => transportApi.addLeg(unitId, eventId, body)}
      onDone={onDone}
    />
  );
}

/** What a leg is called and where it goes — the same boxes whether it is being added or corrected. */
const LEG_WORDS: Record<LegKind, string> = {
  Outbound: 'Getting there',
  Return: 'Getting home',
  Excursion: 'A trip during the event',
};

/** What a leg's `returnsAt` is, which is a different fact on each of the three. */
const SECOND_TIME_WORDS: Record<LegKind, string> = {
  Outbound: 'Arrives at',
  Return: 'Gets back at',
  Excursion: 'Back at the site at',
};

function LegFields({
  kind,
  onKindChange,
  available,
  timeZoneId,
  submitLabel,
  busy,
  initial,
  onSubmit,
  onDone,
}: {
  kind: LegKind;
  /** Absent in edit mode, which is what makes the kind unchangeable rather than merely discouraged. */
  onKindChange?: (kind: LegKind) => void;
  available?: LegKind[];
  /** The event's zone. Both times are read and written as wall-clock readings against it, never the browser's. */
  timeZoneId: string;
  submitLabel: string;
  busy: boolean;
  initial?: TransportLeg;
  onSubmit: (body: {
    kind: LegKind;
    label: string | null;
    departsAt: string | null;
    returnsAt: string | null;
    originText: string | null;
    destinationText: string | null;
  }) => Promise<unknown>;
  onDone: (saved: boolean) => Promise<void>;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');

  // The stored instant back to the reading the leader typed, in the event's zone. Prefilling from
  // the browser's zone would open the form on a departure an hour out and saving it would make that
  // wrong time the record -- which is rule 5's failure arriving through an edit form.
  const [departsAt, setDepartsAt] = useState(
    initial?.departsAt ? toWallClockInZone(initial.departsAt, timeZoneId) : '',
  );
  const [returnsAt, setReturnsAt] = useState(
    initial?.returnsAt ? toWallClockInZone(initial.returnsAt, timeZoneId) : '',
  );
  const [originText, setOriginText] = useState(initial?.originText ?? '');
  const [destinationText, setDestinationText] = useState(initial?.destinationText ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  return (
    <form
      className="flex flex-col gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
          await onSubmit({
            kind,
            label: label.trim() || null,
            departsAt: departsAt || null,
            returnsAt: returnsAt || null,
            originText: originText.trim() || null,
            destinationText: destinationText.trim() || null,
          });
          await onDone(true);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'That did not save.');
        } finally {
          setSaving(false);
        }
      }}
    >
      {onKindChange && available && (
        <SelectField
          label="Which trip"
          value={kind}
          onChange={(e) => onKindChange(e.target.value as LegKind)}
        >
          {available.map((k) => (
            <option key={k} value={k}>
              {LEG_WORDS[k]}
            </option>
          ))}
        </SelectField>
      )}

      <TextField
        label="What to call it"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        required={kind === 'Excursion'}
        hint={
          kind === 'Excursion'
            ? 'Required — “Saturday hike shuttle”. Two unlabelled ones look identical.'
            : 'Optional. Left blank it reads “' + LEG_WORDS[kind] + '”.'
        }
      />

      <TextField
        label="Leaves at"
        type="datetime-local"
        value={departsAt}
        onChange={(e) => setDepartsAt(e.target.value)}
        hint="Local time where the event is."
      />

      {/*
        `returnsAt` is the far end of *this* leg, which is a different fact on each kind: arriving at
        camp on the way there, being home on the way back, and returning to the site after an
        excursion. Labelling all three "Gets back at" -- the round-trip form's wording, where it can
        only mean home -- invites a leader editing "Getting there" to type Sunday evening into it,
        which saves cleanly and puts a nonsense span on the card.
      */}
      <TextField
        label={SECOND_TIME_WORDS[kind]}
        type="datetime-local"
        value={returnsAt}
        onChange={(e) => setReturnsAt(e.target.value)}
        hint="Optional."
      />

      <TextField label="From" value={originText} onChange={(e) => setOriginText(e.target.value)} />
      <TextField
        label="To"
        value={destinationText}
        onChange={(e) => setDestinationText(e.target.value)}
      />

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabledReason={busy || saving ? 'Saving…' : undefined}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={() => void onDone(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
